
import React, { useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import LocationHero from '@/components/locations/LocationHero';
import LocationFeatures from '@/components/locations/LocationFeatures';
import LocationTestimonials from '@/components/locations/LocationTestimonials';
import LocationCTA from '@/components/locations/LocationCTA';
import LocationDetail from '@/components/locations/LocationDetail';
import LocationMap from '@/components/locations/LocationMap';
import { Helmet } from 'react-helmet-async';
import { preloadCriticalImages } from '@/utils/optimizeImages';

const NorrebroLocation = () => {
  // Preload the location image when the component mounts
  useEffect(() => {
    preloadCriticalImages(['/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png']);
  }, []);

  const testimonials = React.useMemo(() => [
    {
      name: "Mikkel Hansen",
      location: "Nørrebro",
      text: "Oscar er en fantastisk klaverlærer! Hans undervisning er altid tilpasset mit niveau og mine interesser, hvilket gør det både udfordrende og sjovt at lære.",
      stars: 5
    },
    {
      name: "Sara Jensen",
      location: "Nørrebro",
      text: "Jeg har prøvet flere forskellige klaverlærere, men ingen har kunnet måle sig med Oscars pædagogiske tilgang og engagement. Han forstår virkelig at formidle både det tekniske og det musikalske.",
      stars: 5
    },
    {
      name: "Thomas Larsen",
      location: "Nørrebro",
      text: "Min datter på 9 år elsker at gå til klaver hos Oscar. Han formår at holde undervisningen engagerende og sjov, samtidig med at han stiller krav og skaber resultater.",
      stars: 5
    }
  ], []);

  const features = React.useMemo(() => [
    {
      title: "Nem transport fra Nørrebro",
      description: "God forbindelse fra Nørrebro med buslinje 6A.",
      icon: "map-pin"
    },
    {
      title: "Fleksible undervisningstider",
      description: "Tider der passer til din hverdag - både dag, aften og weekend.",
      icon: "clock"
    },
    {
      title: "Alle aldersgrupper",
      description: "Undervisning for børn fra 6 år, unge og voksne på alle niveauer.",
      icon: "users"
    },
    {
      title: "Konservatorieuddannet",
      description: "Undervisning af høj kvalitet fra Oscar Gilbert, som er uddannet på Rytmisk Musikkonservatorium.",
      icon: "award"
    }
  ], []);

  const descriptionContent = (
    <>
      <p className="text-lg text-gray-700 mb-6">
        Oscar Gilbert tilbyder professionel klaverundervisning for elever på Nørrebro. Som konservatorieuddannet pianist giver Oscar undervisning af høj kvalitet, der er tilpasset netop dit niveau og dine musikalske mål.
      </p>
      <p className="text-lg text-gray-700 mb-6">
        Uanset om du er nybegynder eller øvet, barn eller voksen, får du individuel vejledning, der hjælper dig med at udvikle dine færdigheder på klaveret.
      </p>
      <p className="text-lg text-gray-700 mb-8">
        Undervisningen foregår i rolige og inspirerende omgivelser, hvor du kan koncentrere dig fuldt ud om at lære og udvikle dig musikalsk.
      </p>
    </>
  );

  const transportInfo = (
    <>
      <p className="text-gray-700">• Med bus 6A</p>
      <p className="text-gray-700">• Med S-tog til Vangede Station + 5 min gang</p>
    </>
  );

  return (
    <Layout>
      <div id="top"></div>
      <Helmet>
        <title>Klaverundervisning på Nørrebro | Få en gratis prøvetime!</title>
        <meta name="description" content="Professionel klaverundervisning på Nørrebro med Oscar Gilbert. Book en gratis prøvetime og start din musikalske rejse i dag!" />
        {/* Add structured data for better SEO */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MusicSchool",
              "name": "Oscar Gilbert Klaverundervisning - Nørrebro",
              "description": "Professionel klaverundervisning på Nørrebro med Oscar Gilbert",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Niels Finsens Alle 86",
                "addressLocality": "Søborg",
                "postalCode": "2860",
                "addressCountry": "DK"
              },
              "areaServed": {
                "@type": "City",
                "name": "Nørrebro, København"
              },
              "telephone": "+4540817127",
              "url": "https://pianoundervisning.dk/klaverundervisning-norrebro"
            }
          `}
        </script>
        {/* Add resource preloads for critical resources */}
        <link rel="preload" as="image" href="/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png" />
      </Helmet>
      
      <LocationHero 
        title="Klaverundervisning på Nørrebro"
        description="Professionel klaverundervisning med Oscar Gilbert for elever på Nørrebro. Undervisning for alle aldre og niveauer med let adgang med offentlig transport."
      />
      
      <LocationDetail 
        locationName="Nørrebro"
        description={descriptionContent}
        imageSrc="/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png"
        imageAlt="Klaverundervisning på Nørrebro"
      />
      
      <LocationFeatures features={features} />
      
      <LocationTestimonials testimonials={testimonials} locationName="Nørrebro" title="Hvad siger mine elever på Nørrebro" />
      
      <LocationMap 
        locationName="Nørrebro"
        address={{
          street: "Niels Finsens Alle 86",
          city: "Søborg",
          postalCode: "2860"
        }}
        transportInfo={transportInfo}
        mapUrl="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d71893.0078265522!2d12.5220869!3d55.7297216!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465251f89eae19fd%3A0x1029a319978e54b7!2sNiels%20Finsens%20Alle%2086%2C%202860%20S%C3%B8borg!5e0!3m2!1sda!2sdk!4v1743771839328!5m2!1sda!2sdk"
        contactPhone="+4540817127"
        contactEmail="kontakt@pianoundervisning.dk"
      />
      
      <LocationCTA 
        locationName="Nørrebro" 
        ctaTitle="Start din klaverrejse på Nørrebro i dag" 
        ctaDescription="Book en gratis prøvetime og oplev forskellen med professionel klaverundervisning fra Oscar Gilbert på Nørrebro."
      />
    </Layout>
  );
};

// Export a memoized version to prevent unnecessary re-renders
export default React.memo(NorrebroLocation);
